import React from 'react';
import {
  PipelineState,
  WalkthroughBackupFileContents,
} from 'src/pages/Settings/components/WalkthroughBackupUploader/Hooks/useWalkthroughBackupUpload';
import Button from 'src/components/Button/Button';
import './ErrorCard.scss';

interface ErrorCardProps {
  state: PipelineState;
  missingFields: string[];
  errorDetail: string | null;
  fileContents: WalkthroughBackupFileContents | null;
  onReset: () => void;
}

export const ErrorCard = ({
  state,
  missingFields,
  errorDetail,
  fileContents,
  onReset,
}: ErrorCardProps) => (
  <div className="ec_container">
    <div className="ec_icon_wrap">
      <span className="ec_icon">✕</span>
    </div>
    <p className="ec_headline">Something went wrong</p>
    {state === 'error-invalid-json' && (
      <>
        <div className="ec_message ec_message--error">
          This file doesn't appear to be a valid walkthrough backup. Please
          check you've selected the correct file and try again.
        </div>
        {errorDetail && <div className="ec_detail_block">{errorDetail}</div>}
      </>
    )}
    {state === 'error-missing-fields' && (
      <>
        <div className="ec_message ec_message--error">
          The backup file is missing required fields and cannot be restored.
          The following fields were not found:
        </div>
        {missingFields.map((field: string) => (
          <div key={field} className="ec_field_row">
            <span className="ec_field_badge">{field}</span>
            <span className="ec_field_status">missing</span>
          </div>
        ))}
      </>
    )}
    {state === 'error-not-eligible' && (
      <>
        <div className="ec_message ec_message--error">
          This walkthrough has already been completed and cannot be overwritten.
          Upload blocked to prevent data loss.
        </div>
        {fileContents && (
          <div className="ec_detail_table">
            <div className="ec_detail_row">
              <span>Walkthrough ID</span>
              <span>{fileContents.walkthroughId}</span>
            </div>
            <div className="ec_detail_row">
              <span>Status</span>
              <span className="ec_status_value">Completed &amp; synced</span>
            </div>
          </div>
        )}
        <div className="ec_detail_block ec_detail_block--muted">
          If you believe this is an error, please contact Juniper Support.
        </div>
      </>
    )}
    {state === 'error-network' && (
      <>
        <div className="ec_message ec_message--error">
          {errorDetail ?? 'Unable to reach the server. Please check your connection and try again.'}
        </div>
        <div className="ec_detail_block ec_detail_block--muted">
          If the problem persists, contact Juniper support.
        </div>
      </>
    )}
    {state === 'error-upload-failed' && (
      <>
        <div className="ec_message ec_message--error">
          The file could not be uploaded. Please try again — if the problem
          persists, please contact Juniper support.
        </div>
        {errorDetail && <div className="ec_detail_block">{errorDetail}</div>}
      </>
    )}
    <Button label="Start Over" onClick={onReset} outlined size="small" />
  </div>
);